import { useTranslation } from 'react-i18next';

import { PROPERTY_TYPES } from '@/lib/db/deals';
import { discountBadgeLabelKey, discountDisplayMode } from '@/lib/discount-display';
import { formatDiscountMagnitude } from '@/lib/format';

import './DealMediaBadges.css';

export type DealMediaBadgesProps = {
  propertyType?: string | null;
  discountPct?: number | null;
  dldConfidence?: string | null;
  dldScope?: string | null;
  /** `card` = compact (catalogue cards) · `feature` = larger (deal-page main photo). */
  size?: 'card' | 'feature';
};

// Shared overlay badges for a deal photo: the property-type label (top-left,
// soft gold) and the red "X% Below Market" discount headline (top-right).
// Single source of truth so the catalogue card and the deal-page main photo
// never diverge. The parent element MUST be `position: relative`. Rectangular
// (ADR-054). Both badges self-gate — nothing renders when the data does not
// warrant it (unknown property type; no / 0 / negative / unscored discount).
export function DealMediaBadges({
  propertyType,
  discountPct,
  dldConfidence,
  dldScope,
  size = 'card',
}: DealMediaBadgesProps) {
  const { t } = useTranslation();

  const propertyTypeLabel =
    propertyType && (PROPERTY_TYPES as readonly string[]).includes(propertyType)
      ? t(`common:catalogue.filters.propertyTypeOpts.${propertyType}`)
      : null;

  const discountKey = discountBadgeLabelKey(
    discountDisplayMode(dldConfidence, dldScope, discountPct),
  );

  return (
    <>
      {propertyTypeLabel && (
        <div className={`deal-badge deal-badge--type deal-badge--${size}`}>{propertyTypeLabel}</div>
      )}
      {discountKey && (
        <div className={`deal-badge deal-badge--discount deal-badge--${size}`}>
          {t(discountKey, { pct: formatDiscountMagnitude(discountPct ?? 0) })}
        </div>
      )}
    </>
  );
}
